import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';

export const ROLE_HOME = { worker: '/worker', employer: '/employer', agent: '/agent', hr: '/hr', admin: '/admin' };
const ROLE_LABEL = { worker: 'Worker', employer: 'Employer Admin', agent: 'Agency', hr: 'HR Portal', admin: 'Administrator' };

// Shown only to accounts that hold more than one role.
// variant="pill" (default) renders header buttons; variant="menu" renders dropdown rows for phones.
export default function RoleSwitcher({ tone = 'light', variant = 'pill', className = '', onDone }) {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const roles = user?.roles || [];
  if (roles.length < 2) return null;
  const others = roles.filter((r) => r !== user.role);

  const switchTo = async (role) => {
    setBusy(true);
    try {
      const res = await apiCall('/api/auth/switch-role', { method: 'POST', body: JSON.stringify({ role }) });
      updateUser(res.user);
      toast.success(`Switched to ${ROLE_LABEL[role] || role}`);
      onDone?.();
      navigate(ROLE_HOME[role] || '/');
    } catch (e) {
      toast.error(e?.message || 'Could not switch role');
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'menu') {
    return (
      <div className={`border-b border-slate-100 pb-1 mb-1 ${className}`} data-testid="role-switcher-menu">
        {others.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => switchTo(r)}
            disabled={busy}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-indigo-50 transition-colors text-left disabled:opacity-60"
            data-testid={`menu-switch-to-${r}-btn`}
          >
            <span className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />}
            </span>
            <span>
              <span className="block text-sm font-semibold text-slate-900">Switch to {ROLE_LABEL[r] || r}</span>
              <span className="block text-xs text-slate-500">You're currently in the {ROLE_LABEL[user.role] || user.role} view</span>
            </span>
          </button>
        ))}
      </div>
    );
  }

  const base = tone === 'dark'
    ? 'bg-white/10 text-white hover:bg-white/20 border-white/20'
    : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200';

  return (
    <div className={`flex items-center gap-1.5 ${className}`} data-testid="role-switcher">
      {others.map((r) => (
        <button
          key={r}
          type="button"
          onClick={() => switchTo(r)}
          disabled={busy}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors disabled:opacity-60 ${base}`}
          data-testid={`switch-to-${r}-btn`}
          title={`Switch to ${ROLE_LABEL[r] || r}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowLeftRight className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">Switch to </span>{ROLE_LABEL[r] || r}
        </button>
      ))}
    </div>
  );
}
